import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";

export default function PassClaim() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/pass/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(e.response?.data?.detail || "This pass link isn't valid"));
  }, [token]);

  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/passes/${token}/claim`);
      await refresh?.();
      toast.success(`Premium is on until ${new Date(data.ends_at).toLocaleDateString(undefined, { day: "numeric", month: "long" })}`);
      navigate(user?.trinity ? "/today" : "/welcome");
    } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong. Please try again."); setBusy(false); }
  };
  const months = info ? `${info.months} month${info.months > 1 ? "s" : ""}` : "";
  const next = encodeURIComponent(`/pass/${token}`);
  const wrongEmail = user && info && (user.email || "").toLowerCase() !== info.email;

  return (
    <PublicShell testId="pass-claim-page">
      <div className={`px-5 pt-6 fade-up ${WIDE ? "lg:max-w-xl lg:mx-auto lg:pt-14" : ""}`}>
        {error ? <p data-testid="pass-claim-error" className="text-center text-[#A7B0C0] py-20">{error}</p> : !info ? (
          <div className="flex justify-center py-24"><Loader2 className="animate-spin gold-text" size={28} /></div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Crown size={11} className="inline -mt-0.5 mr-1" />A gift from Astra</p>
            <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-3" data-testid="pass-claim-title">{info.name}, enjoy {months} of Premium, free</h1>
            {info.note && <p className="text-lg italic text-[#E8DCC4] mb-3" data-testid="pass-claim-note">“{info.note}”</p>}
            <p className="text-base text-[#A7B0C0] mb-7">Horoscopes written for your exact birth chart, unlimited questions and every reading in Explore. No card needed and nothing renews.</p>
            <div className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-3xl p-6 space-y-3" data-testid="pass-claim-box">
              {info.claimed ? (
                <p className="text-base text-[#D6D0C5]" data-testid="pass-claimed">
                  {user && (user.email || "").toLowerCase() === info.email && info.ends_at
                    ? <>Your Premium is active until <span className="gold-text">{new Date(info.ends_at).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}</span>. <Link to="/today" className="gold-text underline underline-offset-4">Open today's reading</Link></>
                    : "This pass has already been claimed."}
                </p>
              ) : !user ? (
                <>
                  <p className="text-base text-[#D6D0C5]">This pass is for <span className="gold-text">{info.email}</span>. Create your free account or sign in with that email to claim it.</p>
                  <Link to={`/join?next=${next}`} data-testid="pass-claim-join" className="block text-center bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors">Create my free account</Link>
                  <Link to={`/join?mode=login&next=${next}`} data-testid="pass-claim-signin" className="block text-center gold-text">I already have an account</Link>
                </>
              ) : wrongEmail ? (
                <div data-testid="pass-wrong-email" className="space-y-3">
                  <p className="text-base text-[#D6D0C5]">You're signed in as {user.email}. This pass is for <span className="gold-text">{info.email}</span>, so please sign out and sign in with that address.</p>
                  <button data-testid="pass-sign-out" onClick={() => logout()} className="w-full border border-[#D4AF37]/40 gold-text text-lg py-3 rounded-full hover:bg-[#D4AF37]/10 transition-colors">Sign out</button>
                </div>
              ) : (
                <button data-testid="btn-pass-claim" onClick={claim} disabled={busy}
                  className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={18} /> : <Crown size={18} />} Claim my free Premium
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
